import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { matchesTerms, searchTerms } from "./searchMatch";
import { normalizeHex, readableTextColor } from "./routeColors";
import { useAnchoredMenu } from "./useAnchoredMenu";
import { usePendingCommit } from "./usePendingCommit";
import type { Option } from "@/types/domain";

/**
 * 표 셀 인라인 편집용 드롭다운.
 * 요구사항 §9-3 — 범주형 입력은 **전부** dropdown + 검색이므로 검색창이 항상 붙는다.
 * 평상시엔 라벨만 보이고, 클릭하면 검색 input + 후보 목록이 열린다.
 *
 * 고른 값이 서버에서 돌아올 때까지는 점선 밑줄로 남는다 (설계원칙 P20 — `usePendingCommit`).
 */
export interface InlineDropdownProps<V extends string | number = string> {
  value: V | null | undefined;
  options: readonly (V | Option<V>)[];
  onChange: (value: V) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** 값 없음을 고를 수 있게 한다 */
  allowEmpty?: boolean;
  emptyLabel?: string;
}

function normalize<V extends string | number>(
  options: readonly (V | Option<V>)[]
): Option<V>[] {
  return options.map((opt) =>
    typeof opt === "object" && opt !== null && "value" in opt
      ? (opt as Option<V>)
      : ({ label: String(opt), value: opt as V } as Option<V>)
  );
}

export default function InlineDropdown<V extends string | number = string>({
  value,
  options,
  onChange,
  placeholder = "선택",
  className = "",
  disabled = false,
  allowEmpty = false,
  emptyLabel = "(없음)",
}: InlineDropdownProps<V>) {
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  // 목록은 body 로 포털해 표의 overflow 컨테이너에 잘리지 않게 한다.
  const { anchorRef, menuRef, style, reposition, isOutside } = useAnchoredMenu(editing);
  const searchRef = useRef<HTMLInputElement>(null);
  const { pending, markPending } = usePendingCommit(value);

  const normalized = useMemo(() => normalize(options), [options]);
  const selected = normalized.find((opt) => opt.value === value);

  // 화살표·괄호·`_` 를 무시하고 대조한다 — `포스코→한라시멘트` 를 `포스코한` 으로 찾는다
  const filtered = useMemo(() => {
    const terms = searchTerms(search);
    if (terms.length === 0) return normalized;
    return normalized.filter((opt) => matchesTerms(terms, opt.label, opt.hint));
  }, [normalized, search]);

  useEffect(() => {
    if (editing) {
      reposition();
      searchRef.current?.focus();
    }
  }, [editing, reposition]);

  useEffect(() => {
    if (!editing) return;
    const onDown = (e: MouseEvent) => {
      if (isOutside(e.target as Node)) {
        setEditing(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editing, isOutside]);

  const select = useCallback(
    (next: V) => {
      if (next !== value) markPending();
      onChange(next);
      setEditing(false);
      setSearch("");
    },
    [onChange, value, markPending]
  );

  if (disabled) {
    return (
      <span className={className}>
        {selected?.label ?? <span className="text-gray-300">{placeholder}</span>}
      </span>
    );
  }

  if (editing) {
    return (
      <div ref={containerRef} className="inline-block w-full">
        <input
          ref={(el) => {
            searchRef.current = el;
            anchorRef.current = el;
          }}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEditing(false);
              setSearch("");
            } else if (e.key === "Enter" && filtered.length > 0) {
              select(filtered[0].value);
            }
          }}
          placeholder={selected?.label ?? placeholder}
          className="w-full min-w-[6rem] rounded border border-gray-400 bg-white px-1 py-0.5 text-sm focus:border-primary-600 focus:outline-none"
        />
        {style &&
          createPortal(
            <div
              ref={menuRef}
              style={style}
              className="overflow-y-auto border border-gray-300 bg-white shadow-sm"
            >
          {allowEmpty && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                select("" as V);
              }}
              className="block w-full px-2 py-1.5 text-left text-sm text-gray-400 hover:bg-gray-100"
            >
              {emptyLabel}
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-2 py-2 text-xs text-gray-400">일치하는 항목 없음</div>
          ) : (
            filtered.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                disabled={opt.disabled}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(opt.value);
                }}
                className={`block w-full px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:text-gray-300 ${
                  opt.value === value ? "bg-primary-50 font-medium text-primary-800" : "text-gray-700"
                }`}
              >
                <ColorText color={opt.color}>{opt.label}</ColorText>
                {opt.hint && (
                  <span className="ml-1.5 shrink-0 whitespace-nowrap text-xs text-gray-400">{opt.hint}</span>
                )}
              </button>
            ))
          )}
            </div>,
            document.body
          )}
      </div>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => {
        setEditing(true);
        setSearch("");
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
      title={pending ? "저장 중" : "클릭하여 수정"}
      className={`-mx-1 inline-block cursor-pointer rounded px-1 transition-colors hover:bg-gray-100 ${
        pending ? "hj-pending" : ""
      } ${className}`}
    >
      {selected ? (
        <ColorText color={selected.color}>{selected.label}</ColorText>
      ) : (
        <span className="text-gray-300">{placeholder}</span>
      )}
    </span>
  );
}

/**
 * 값이 색을 갖고 있으면 그 색을 **글자 배경**으로 깐다 (`RouteChip` 과 같은 규칙).
 *
 * 색이 없는 것이 기본이다 — 그때는 그냥 글자다. 어두운 색에서는 상대휘도로 글자를
 * 흰색으로 뒤집는다.
 */
export function ColorText({
  color,
  children,
}: {
  color?: string | null;
  children: ReactNode;
}) {
  const hex = normalizeHex(color);
  if (!hex) return <span className="truncate">{children}</span>;
  return (
    <span
      className="inline-block max-w-full truncate rounded px-1.5 py-0.5"
      style={{ backgroundColor: hex, color: readableTextColor(hex) }}
    >
      {children}
    </span>
  );
}
